import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { getConsoleAlarms } from '../../lib/api'
import { unacknowledgedCount } from '../../lib/consoleStatus'
import { cn } from '../../lib/utils'

// BRIEF-F1b item 2: the staff bell is the count of unacknowledged platform
// alarms (last 14 days, /hub/console/alarms) and it opens the console's
// Alarms section. null = not known: no number is shown rather than a guess.

export function AlarmBell({ count, className }: { count: number | null; className?: string }) {
  const label =
    count === null
      ? 'Alarms: count not known'
      : count === 0
        ? 'Alarms: none waiting to be acknowledged'
        : `Alarms: ${count} not acknowledged`
  return (
    <Link
      to="/console#alarms"
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-[#d9cdb8] hover:text-white',
        className,
      )}
    >
      <Bell className="h-5 w-5" aria-hidden="true" />
      {count !== null && count > 0 && (
        <span
          data-testid="alarm-bell-count"
          className="absolute right-0.5 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-bold text-white"
        >
          {count}
        </span>
      )}
    </Link>
  )
}

// The bell on the older staff pages: reads the alarms itself. A failed read
// shows the bell with no number; it never invents one.
export function SelfLoadingAlarmBell({ className }: { className?: string }) {
  const [count, setCount] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const alarms = await getConsoleAlarms()
        if (!cancelled) setCount(unacknowledgedCount(alarms))
      } catch {
        if (!cancelled) setCount(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])
  return <AlarmBell count={count} className={className} />
}
